import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Loader2, ArrowLeft, User as UserIcon, ShieldAlert, LogOut, KeyRound, UserX, RefreshCw, UserCheck } from "lucide-react";
import { toast } from "sonner";

import Sidebar from "@/components/Sidebar";
import Header from "@/components/Header";
import { apiFetch } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { isAdminRole } from "@/data/v12_user_nav";

function Section({ title, children, testid }) {
    return (
        <div data-testid={testid} className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-5">
            <h2 className="text-sm font-semibold text-text-main mb-3">{title}</h2>
            {children}
        </div>
    );
}

function Row({ label, value }) {
    return (
        <div className="flex items-center justify-between gap-3 text-xs py-1">
            <span className="text-text-secondary uppercase tracking-widest">{label}</span>
            <span className="text-text-main tabular-nums truncate max-w-[240px]">{value ?? "—"}</span>
        </div>
    );
}

export default function AdminUserDetail() {
    const { id } = useParams();
    const nav = useNavigate();
    const { user: me } = useAuth();
    const [detail, setDetail] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            setDetail(await apiFetch(`/admin/users/${id}`));
        } catch (e) { toast.error(e.message); }
        finally { setLoading(false); }
    }, [id]);

    useEffect(() => { load(); }, [load]);

    const doPatch = async (patch) => {
        setBusy(true);
        try {
            const res = await apiFetch(`/admin/users/${id}`, { method: "PATCH", body: JSON.stringify(patch) });
            toast.success("Updated.");
            await load();
            return res;
        } catch (e) { toast.error(e.message); }
        finally { setBusy(false); }
    };

    const changePlan = async () => {
        const p = prompt("New plan (free/starter/pro/business/enterprise):", detail?.profile?.plan_id || "free");
        if (!p) return;
        await doPatch({ plan_id: p });
    };

    const grantCredits = async () => {
        const cur = detail?.profile?.credits || 0;
        const raw = prompt(`Set credits (current: ${cur}). Enter absolute value or +N/-N:`, String(cur));
        if (raw === null) return;
        let target = cur;
        if (raw.trim().startsWith("+")) target = cur + parseInt(raw.slice(1), 10);
        else if (raw.trim().startsWith("-")) target = cur - parseInt(raw.slice(1), 10);
        else target = parseInt(raw, 10);
        if (isNaN(target)) return toast.error("Invalid number");
        await doPatch({ credits: target });
    };

    const adjustReputation = async () => {
        const cur = detail?.profile?.reputation || 0;
        const raw = prompt(`Reputation (current: ${cur}). Absolute or +N/-N:`, String(cur));
        if (raw === null) return;
        let target = cur;
        if (raw.trim().startsWith("+")) target = cur + parseInt(raw.slice(1), 10);
        else if (raw.trim().startsWith("-")) target = cur - parseInt(raw.slice(1), 10);
        else target = parseInt(raw, 10);
        if (isNaN(target)) return toast.error("Invalid number");
        await doPatch({ reputation: target });
    };

    const promoteAdmin = async () => {
        if (!window.confirm("Promote this user to admin? This grants full technical bypass.")) return;
        await doPatch({ role: "admin", confirm_role_escalation: true });
    };
    const demoteAdmin = async () => {
        if (!window.confirm("Demote admin to regular user?")) return;
        await doPatch({ role: "user" });
    };

    const suspend = async () => {
        const reason = prompt("Suspend reason:");
        if (!reason) return;
        setBusy(true);
        try {
            await apiFetch(`/admin/users/${id}/suspend`, { method: "POST", body: JSON.stringify({ reason }) });
            toast.success("Suspended.");
            await load();
        } catch (e) { toast.error(e.message); } finally { setBusy(false); }
    };
    const unsuspend = async () => {
        setBusy(true);
        try {
            await apiFetch(`/admin/users/${id}/unsuspend`, { method: "POST", body: JSON.stringify({ reason: "restored by admin" }) });
            toast.success("Restored.");
            await load();
        } catch (e) { toast.error(e.message); } finally { setBusy(false); }
    };

    const forceLogout = async () => {
        if (!window.confirm("Invalidate ALL sessions of this user?")) return;
        setBusy(true);
        try {
            const r = await apiFetch(`/admin/users/${id}/force-logout`, { method: "POST", body: JSON.stringify({}) });
            toast.success(`Killed ${r.sessions_deleted} sessions.`);
            await load();
        } catch (e) { toast.error(e.message); } finally { setBusy(false); }
    };

    const resetPassword = async () => {
        setBusy(true);
        try {
            await apiFetch(`/admin/users/${id}/reset-password`, { method: "POST" });
            toast.success("Reset requested — user notified.");
        } catch (e) { toast.error(e.message); } finally { setBusy(false); }
    };

    const impersonate = async () => {
        if (!window.confirm("Impersonate is DEV-ONLY. Continue?")) return;
        try {
            const r = await apiFetch(`/admin/users/${id}/impersonate`, { method: "POST" });
            toast.success("Impersonation session created (1h). Token in browser storage.");
            localStorage.setItem("glms_impersonation_token", r.session_token || "");
        } catch (e) { toast.error(e.message); }
    };

    const hardDelete = async () => {
        const step1 = window.confirm("HARD DELETE user + all their data. This is IRREVERSIBLE. Continue?");
        if (!step1) return;
        const conf = prompt('Type DELETE to confirm:');
        if (conf !== "DELETE") return toast.error("Not confirmed.");
        setBusy(true);
        try {
            await apiFetch(`/admin/users/${id}`, { method: "DELETE", body: JSON.stringify({ confirmation_text: "DELETE" }) });
            toast.success("Deleted.");
            nav("/admin/users");
        } catch (e) { toast.error(e.message); } finally { setBusy(false); }
    };

    if (!isAdminRole(me?.role)) {
        return <div className="min-h-screen bg-[var(--color-bg)] text-text-main flex items-center justify-center"><h1 className="text-2xl">Admin only</h1></div>;
    }

    return (
        <div className="min-h-screen bg-[var(--color-bg)] text-text-main flex">
            <Sidebar />
            <div className="flex-1 flex flex-col min-w-0">
                <Header />
                <main className="flex-1 p-6 md:p-8 max-w-6xl mx-auto w-full">
                    <div className="mb-4">
                        <Link to="/admin/users" data-testid="user-detail-back" className="text-xs text-text-secondary hover:text-text-main inline-flex items-center gap-1">
                            <ArrowLeft className="h-3 w-3" /> Back to users
                        </Link>
                    </div>

                    {loading || !detail ? (
                        <div className="flex items-center justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[var(--color-primary)]" /></div>
                    ) : (
                        <div className="grid gap-6 lg:grid-cols-3" data-testid="user-detail">
                            <div className="lg:col-span-2 space-y-6">
                                <Section title="Profile" testid="user-detail-profile">
                                    <div className="flex items-center gap-4 mb-4">
                                        {detail.profile.picture ? (
                                            <img src={detail.profile.picture} alt="" className="h-12 w-12 rounded-full" />
                                        ) : (
                                            <div className="h-12 w-12 rounded-full bg-[var(--color-panel-ai)] flex items-center justify-center">
                                                <UserIcon className="h-5 w-5 text-text-secondary" />
                                            </div>
                                        )}
                                        <div>
                                            <div className="text-base font-semibold">{detail.profile.name || "—"}</div>
                                            <div className="text-xs text-text-secondary">{detail.profile.email}</div>
                                        </div>
                                    </div>
                                    <div className="grid gap-1">
                                        <Row label="User ID" value={<code className="font-mono text-[10px]">{detail.profile.id}</code>} />
                                        <Row label="Plan" value={detail.profile.plan_id || "free"} />
                                        <Row label="Role" value={detail.profile.role || "user"} />
                                        <Row label="Credits" value={(detail.profile.credits ?? 0).toLocaleString()} />
                                        <Row label="Reputation" value={detail.profile.reputation ?? 0} />
                                        <Row label="Suspended" value={detail.suspension.is_suspended ? "YES" : "no"} />
                                        <Row label="Throttle multiplier" value={detail.suspension.throttle_multiplier?.toFixed?.(2) ?? "1.00"} />
                                        <Row label="Stripe customer" value={detail.profile.stripe_customer_id || "—"} />
                                        <Row label="Created" value={(detail.profile.created_at || "").slice(0, 19).replace("T", " ")} />
                                    </div>
                                </Section>

                                <Section title="Counters" testid="user-detail-counters">
                                    <div className="grid gap-4 grid-cols-2 sm:grid-cols-5">
                                        {Object.entries(detail.counters).map(([k, v]) => (
                                            <div key={k}>
                                                <div className="text-[10px] uppercase tracking-widest text-text-secondary">{k.replace(/_/g, " ")}</div>
                                                <div className="text-lg font-semibold tabular-nums">{Number(v).toLocaleString()}</div>
                                            </div>
                                        ))}
                                    </div>
                                </Section>

                                <Section title="Recent activity" testid="user-detail-activity">
                                    {detail.recent_activity.length === 0 ? (
                                        <p className="text-xs text-text-secondary">No recent activity.</p>
                                    ) : (
                                        <ul className="space-y-2">
                                            {detail.recent_activity.map((a, i) => (
                                                <li key={i} className="flex items-center justify-between text-xs py-1 border-b border-[var(--color-border)]/40">
                                                    <span className="text-text-secondary">{(a.at || "").slice(0, 19).replace("T", " ")}</span>
                                                    <span className="text-text-main truncate max-w-[380px]">
                                                        {a.kind === "credit_transaction" && `${a.type} · ${a.amount > 0 ? "+" : ""}${a.amount} · ${a.description || ""}`}
                                                        {a.kind === "reputation_event" && `reputation · ${a.delta > 0 ? "+" : ""}${a.delta} · ${a.reason || ""}`}
                                                        {a.kind === "notification" && `notification (${a.type}) · ${a.title}`}
                                                    </span>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </Section>

                                <Section title="Active sessions" testid="user-detail-sessions">
                                    {detail.sessions.length === 0 ? (
                                        <p className="text-xs text-text-secondary">No active sessions.</p>
                                    ) : (
                                        <ul className="space-y-2">
                                            {detail.sessions.map((s, i) => (
                                                <li key={i} className="flex items-center justify-between text-xs py-1 border-b border-[var(--color-border)]/40">
                                                    <span className="text-text-secondary tabular-nums">{(s.created_at || "").slice(0, 19).replace("T", " ")}</span>
                                                    <span className="text-text-main truncate max-w-[280px]" title={s.user_agent}>{s.user_agent || "—"}</span>
                                                    <span className="text-text-secondary text-[10px]"><code>{(s.ip_hash || "").slice(0, 8)}</code></span>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </Section>
                            </div>

                            {/* Actions sidebar */}
                            <div className="space-y-3" data-testid="user-detail-actions">
                                <button data-testid="user-action-change-plan" onClick={changePlan} disabled={busy} className="w-full text-xs rounded-full bg-white hover:bg-[#EDEDED] text-black px-4 py-2 disabled:opacity-50">Change plan</button>
                                <button data-testid="user-action-grant-credits" onClick={grantCredits} disabled={busy} className="w-full text-xs rounded-full border border-[var(--color-border)] text-text-main hover:border-[var(--color-primary)]/40 px-4 py-2">Grant credits</button>
                                <button data-testid="user-action-adjust-reputation" onClick={adjustReputation} disabled={busy} className="w-full text-xs rounded-full border border-[var(--color-border)] text-text-main hover:border-[var(--color-primary)]/40 px-4 py-2">Adjust reputation</button>
                                {!isAdminRole(detail.profile.role) ? (
                                    <button data-testid="user-action-promote" onClick={promoteAdmin} disabled={busy} className="w-full text-xs rounded-full border border-neutral-400/40 text-neutral-400 hover:bg-neutral-400/10 px-4 py-2 inline-flex items-center justify-center gap-1"><ShieldAlert className="h-3 w-3" /> Promote to admin</button>
                                ) : (
                                    <button data-testid="user-action-demote" onClick={demoteAdmin} disabled={busy} className="w-full text-xs rounded-full border border-[var(--color-border)] text-text-secondary hover:text-text-main px-4 py-2 inline-flex items-center justify-center gap-1"><UserIcon className="h-3 w-3" /> Demote to user</button>
                                )}
                                {detail.suspension.is_suspended ? (
                                    <button data-testid="user-action-unsuspend" onClick={unsuspend} disabled={busy} className="w-full text-xs rounded-full border border-[var(--color-success)]/40 text-[var(--color-success)] hover:bg-[var(--color-success)]/10 px-4 py-2 inline-flex items-center justify-center gap-1"><UserCheck className="h-3 w-3" /> Unsuspend</button>
                                ) : (
                                    <button data-testid="user-action-suspend" onClick={suspend} disabled={busy} className="w-full text-xs rounded-full border border-neutral-400/40 text-neutral-400 hover:bg-neutral-400/10 px-4 py-2 inline-flex items-center justify-center gap-1"><UserX className="h-3 w-3" /> Suspend</button>
                                )}
                                <button data-testid="user-action-force-logout" onClick={forceLogout} disabled={busy} className="w-full text-xs rounded-full border border-[var(--color-border)] text-text-secondary hover:text-text-main px-4 py-2 inline-flex items-center justify-center gap-1"><LogOut className="h-3 w-3" /> Force logout</button>
                                <button data-testid="user-action-reset-password" onClick={resetPassword} disabled={busy} className="w-full text-xs rounded-full border border-[var(--color-border)] text-text-secondary hover:text-text-main px-4 py-2 inline-flex items-center justify-center gap-1"><KeyRound className="h-3 w-3" /> Reset password</button>
                                <button data-testid="user-action-impersonate" onClick={impersonate} disabled={busy} className="w-full text-xs rounded-full border border-neutral-400/40 text-neutral-400 hover:bg-neutral-400/10 px-4 py-2">Impersonate (dev-only)</button>
                                <div className="border-t border-[var(--color-alert)]/30 pt-3 mt-4">
                                    <div className="text-[10px] uppercase tracking-widest text-[var(--color-alert)] mb-2">Danger zone</div>
                                    <button data-testid="user-action-delete" onClick={hardDelete} disabled={busy} className="w-full text-xs rounded-full bg-[var(--color-alert)]/10 border border-[var(--color-alert)]/40 text-[var(--color-alert)] hover:bg-[var(--color-alert)]/20 px-4 py-2">Hard delete (GDPR)</button>
                                </div>
                            </div>
                        </div>
                    )}
                </main>
            </div>
        </div>
    );
}
